/**
 * Header Settings menu (#2242), the gear just before the Help menu: the venue scope (testing override, #689; hidden
 * when the build locks the venue) and the Markets view (grouped by question / all outcomes, #690) and layout (tiles /
 * compact, #1953). These used to sit in the page bar and above the Markets list.
 */
import { useRef, useState } from 'react';
import { CATALOG_LAYOUTS } from '../catalog/layout';
import { CATALOG_VIEWS, useCatalogLayout, useCatalogView } from '../catalog/prefs';
import { useT } from '../i18n/useT';
import VenueDialog from '../venue/VenueDialog';
import { venueLabel } from '../venue/scope';
import { useVenueScope } from '../venue/useVenueScope';
import { useMenu } from './useMenu';

const ITEM = 'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-start text-sm hover:bg-surface-2';
const RADIO = 'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-start text-sm hover:bg-surface-2';
const GROUP_LABEL = 'px-3 pb-0.5 pt-1.5 text-xs font-medium text-muted';

function GearIcon() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function Check({ on }: { on: boolean }) {
  return (
    <span aria-hidden className="w-3 text-accent">
      {on ? '✓' : ''}
    </span>
  );
}

export default function SettingsMenu() {
  const t = useT();
  const { open, setOpen, ref } = useMenu();
  const [venueOpen, setVenueOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scope = useVenueScope();
  const [view, setView] = useCatalogView();
  const [layout, setLayout] = useCatalogLayout();
  const scoped = scope.venue !== null;

  return (
    <div ref={ref} className="relative" data-tour="settings">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('shell.settings')}
        title={t('shell.settings')}
        onClick={() => setOpen((o) => !o)}
        data-testid="settings-menu"
        className="relative flex h-7 w-7 items-center justify-center rounded-full border border-border text-muted hover:bg-surface-2 hover:text-fg"
      >
        <GearIcon />
        {/* Scoped to one venue: a dot on the gear, so testers still see it at a glance with the menu closed. */}
        {scoped && !scope.isLocked ? (
          <span aria-hidden data-testid="settings-venue-dot" className="absolute -end-0.5 -top-0.5 h-2 w-2 rounded-full bg-accent" />
        ) : null}
      </button>
      {open && (
        <div
          role="menu"
          aria-label={t('shell.settings')}
          // Phones: across the screen just under the header (a 16rem menu anchored at the gear could run off the left
          // edge). The header's backdrop filter makes it the containing block of `fixed`.
          className="card fixed inset-x-2 top-full z-30 mt-1 p-1 shadow-xl sm:absolute sm:inset-x-auto sm:end-0 sm:top-auto sm:mt-2 sm:w-64"
        >
          {scope.isLocked ? null : (
            <>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="dialog"
                title={t('venue.button_title')}
                data-testid="venue-picker-button"
                className={ITEM}
                onClick={() => {
                  setOpen(false);
                  setVenueOpen(true);
                }}
              >
                <span>{t('venue.title')}</span>
                <span className={`truncate text-xs ${scoped ? 'font-medium text-accent' : 'text-muted'}`} translate={scoped ? 'no' : undefined}>
                  {scoped ? venueLabel(scope.venue) : t('venue.all')}
                </span>
              </button>
              <div className="my-1 border-t border-border" />
            </>
          )}
          <div role="group" aria-label={t('catalog.view_group')}>
            <div aria-hidden className={GROUP_LABEL}>
              {t('catalog.view')}
            </div>
            {CATALOG_VIEWS.map((v) => (
              <button
                key={v}
                type="button"
                role="menuitemradio"
                aria-checked={view === v}
                className={RADIO}
                data-testid={`settings-view-${v}`}
                onClick={() => {
                  setView(v);
                  setOpen(false);
                }}
              >
                <Check on={view === v} />
                <span>{v === 'grouped' ? t('catalog.view_grouped') : t('catalog.view_flat')}</span>
              </button>
            ))}
          </div>
          <div className="my-1 border-t border-border" />
          <div role="group" aria-label={t('catalog.layout_group')}>
            <div aria-hidden className={GROUP_LABEL}>
              {t('catalog.layout')}
            </div>
            {CATALOG_LAYOUTS.map((l) => (
              <button
                key={l}
                type="button"
                role="menuitemradio"
                aria-checked={layout === l}
                className={RADIO}
                data-testid={`settings-layout-${l}`}
                onClick={() => {
                  setLayout(l);
                  setOpen(false);
                }}
              >
                <Check on={layout === l} />
                <span>{l === 'tiles' ? t('catalog.layout_tiles') : t('catalog.layout_compact')}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <VenueDialog
        open={venueOpen}
        onClose={() => {
          setVenueOpen(false);
          // The menu item that opened the dialog is gone: the gear takes the focus back.
          requestAnimationFrame(() => buttonRef.current?.focus());
        }}
      />
    </div>
  );
}
